import * as React from 'react';
import { Rule, RulesMessages, ValidationResponse } from '../utils/validator';
import TextInput from './TextInput';
import { HatType } from './Hat';

interface PhoneInputProps {
  placeHolder?: string;
  name: string;
  value?: string;
  id: string;
  label: string;
  required?: boolean;
  hasError?: boolean;
  errors?: any;
  theme?: 'night' | 'day';
  validationRules?: Rule[];
  rulesMessages?: RulesMessages;
  onChange?: Function;
  onComplete?: Function;
  onError?: Function;
  onBlur?: Function;
  onEsc?: Function;
  onEnter?: Function;
  onLeft?: Function;
  onRight?: Function;
  onUp?: Function;
  onDown?: Function;
  onSpace?: Function;
  hat?: HatType[];
  readOnly?: boolean;
  disabled?: boolean;
  toolTip?: React.ReactNode;
  autocomplete?: string;
}

interface PhoneInputState {
  inputValue?: string;
  validationResponse?: ValidationResponse;
}

const isModifierKey = (event: any) => {
  const key = event.keyCode;

  return (
    event.shiftKey === true ||
    key === 35 ||
    key === 36 || // Allow Shift, Home, End
    key === 8 ||
    key === 9 ||
    key === 13 ||
    key === 46 || // Allow Backspace, Tab, Enter, Delete
    (key > 36 && key < 41) || // Allow left, up, right, down
    // Allow Ctrl/Command + A,C,V,X,Z
    ((event.ctrlKey === true || event.metaKey === true) &&
      (key === 65 || key === 67 || key === 86 || key === 88 || key === 90))
  );
};

const formatPhoneNumber = (ph: string) => {
  if (!ph) return '';
  let fPh = '';
  for (let i = 0; i < ph.length; i++) {
    if (i === 0) {
      fPh += '(';
    }
    if (i === 3) {
      fPh += ') ';
    }
    if (i === 6) {
      fPh += '-';
    }
    fPh += ph.charAt(i);
  }
  return fPh;
};

const unformatPhoneNumber = (ph: string) => {
  if (!ph) return '';
  return ph.replace(' ', '').replace('(', '').replace(')', '').replace('-', '');
};

export default function PhoneInput(props: PhoneInputProps) {
  const [state, setState] = React.useState<PhoneInputState>({
    inputValue: (props.value && formatPhoneNumber(props.value)) || ''
  });
  const componentPropsRules: Rule[] = [];
  if (props.validationRules) {
    componentPropsRules.push(...props.validationRules);
  }

  if (!componentPropsRules.some((rule) => rule.ruleName === 'phone')) {
    componentPropsRules.push({
      ruleName: 'phone',
      message: props.rulesMessages && props.rulesMessages.phone,
      defaultMessage: `${props.label} is not a valid phone number.`
    });
  }

  React.useEffect(() => {
    if (props.value && formatPhoneNumber(props.value) !== state.inputValue) {
      setState({
        ...state,
        inputValue: props.value && formatPhoneNumber(props.value)
      });
    }
  }, [props.value]);

  return (
    <TextInput
      placeHolder={props.placeHolder || '(XXX) XXX-XXXX'}
      name={props.name}
      value={state.inputValue}
      id={props.id}
      inputMode="tel"
      label={props.label}
      required={props.required}
      hasError={props.hasError}
      errors={props.errors}
      theme={props.theme}
      autocomplete={props.autocomplete}
      onComplete={props.onComplete}
      onError={props.onError}
      onBlur={props.onBlur}
      onChange={(event: any) => {
        if (isModifierKey(event)) {
          return;
        }
        const { target } = event;

        const input = target.value.replace(/\D/g, '').substring(0, 10);
        target.value = null;

        const zip = input.substring(0, 3);
        const middle = input.substring(3, 6);
        const last = input.substring(6, 10);

        if (input.length > 6) {
          target.value = `(${zip}) ${middle}-${last}`;
        } else if (input.length > 3) {
          target.value = `(${zip}) ${middle}`;
        } else if (input.length > 0) {
          target.value = `(${zip}`;
        } else if (target.value === undefined || target.value === null) {
          target.value = '';
        }

        setState({ ...state, inputValue: target.value });
        if (props.onChange !== undefined) {
          props.onChange({
            ...event,
            target: {
              ...event.target,
              value: unformatPhoneNumber(event.target.value) || '',
              valid: event.target.valid || target.value.length === 0
            }
          });
        }
      }}
      onEsc={(inputValue: string) => {
        if (props.onEsc !== undefined) {
          props.onEsc(inputValue);
        }
      }}
      onEnter={(inputValue: string) => {
        if (props.onEnter !== undefined) {
          props.onEnter(inputValue);
        }
      }}
      onUp={(inputValue: string) => {
        if (props.onUp !== undefined) {
          props.onUp(inputValue);
        }
      }}
      onDown={(inputValue: string) => {
        if (props.onDown !== undefined) {
          props.onDown(inputValue);
        }
      }}
      onLeft={(inputValue: string) => {
        if (props.onLeft !== undefined) {
          props.onLeft(inputValue);
        }
      }}
      onRight={(inputValue: string) => {
        if (props.onRight !== undefined) {
          props.onRight(inputValue);
        }
      }}
      onSpace={(inputValue: string) => {
        if (props.onSpace !== undefined) {
          props.onSpace(inputValue);
        }
      }}
      maxLength={14}
      validationRules={componentPropsRules}
      rulesMessages={props.rulesMessages}
      hat={props.hat}
      readOnly={props.readOnly}
      disabled={props.disabled}
      toolTip={props.toolTip}
    />
  );
}
